import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  MapPin, 
  Clock, 
  Share2, 
  Star, 
  Heart,
  ArrowLeft,
  Phone,
  Navigation
} from 'lucide-react';
import { trackEvent } from '../utils/analytics';

interface Campaign {
  id: string;
  name: string;
  message: string;
  zone: string;
  shop_id?: string;
  created_at: string;
  is_active: boolean;
  shop?: {
    name: string;
    description?: string;
    phone?: string;
    location?: string;
  };
}

interface CampaignViewerProps {
  campaignId: string;
}

export default function CampaignViewer({ campaignId }: CampaignViewerProps) {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    console.log('CampaignViewer: Component mounted with campaignId:', campaignId);
    fetchCampaign();
  }, [campaignId]);

  const fetchCampaign = async () => {
    console.log('CampaignViewer: Fetching campaign for ID:', campaignId);
    try {
      console.log('CampaignViewer: Making API request...');
      const response = await fetch(`https://n8n.tenear.com/webhook/manage-campaigns-get`);
      console.log('CampaignViewer: Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('CampaignViewer: API Response data:', data);
      
      if (data.success && data.campaigns) {
        console.log('CampaignViewer: Found campaigns array, length:', data.campaigns.length);
        console.log('CampaignViewer: Looking for campaign ID:', campaignId);
        console.log('CampaignViewer: Available campaign IDs:', data.campaigns.map((c: any) => c.id));
        
        // Find the specific campaign by ID
        const campaign = data.campaigns.find((c: any) => c.id === campaignId);
        console.log('CampaignViewer: Found campaign:', campaign);
        
        if (campaign) {
          setCampaign({
            id: campaign.id,
            name: campaign.title,
            message: campaign.description,
            zone: campaign.location,
            shop_id: campaign.shopId,
            created_at: new Date().toISOString(), // Use current time if not available
            is_active: campaign.isActive,
            shop: {
              name: campaign.locationName || 'Campaign Location',
              description: campaign.plainMessage || '',
              phone: '',
              location: campaign.locationName || campaign.location
            }
          });
          console.log('CampaignViewer: Campaign set successfully');

          // Track page view (scan) when campaign loads
          if (campaign.shopId && campaign.id) {
            trackEvent({ 
              shop_id: campaign.shopId, 
              campaign_id: campaign.id, 
              action: 'scan' 
            });
            console.log('✅ Tracked: page view (scan) for campaign', campaign.id);
          }
        } else {
          console.log('CampaignViewer: Campaign not found in array');
          setError(`Campaign "${campaignId}" not found`);
        }
      } else {
        console.log('CampaignViewer: API returned success: false or no campaigns');
        setError('Failed to load campaign');
      }
    } catch (err) {
      console.error('CampaignViewer: Error fetching campaign:', err);
      setError('Failed to load campaign');
    } finally {
      console.log('CampaignViewer: Setting loading to false');
      setLoading(false);
    }
  };

  const handleShare = async () => {
    // Track share action
    if (campaign?.shop_id && campaign?.id) {
      trackEvent({ 
        shop_id: campaign.shop_id, 
        campaign_id: campaign.id, 
        action: 'share' 
      });
      console.log('✅ Tracked: share for campaign', campaign.id);
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: campaign?.name || 'Campaign',
          text: campaign?.message || '',
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(window.location.href);
      // Show a brief notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-4 py-2 rounded-lg z-50';
      notification.textContent = 'Link copied to clipboard!';
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 2000);
    }
  };

  const handleClaimOffer = async () => {
    // Track claim action with tracking pixel
    if (campaign?.shop_id && campaign?.id) {
      trackEvent({ 
        shop_id: campaign.shop_id, 
        campaign_id: campaign.id, 
        action: 'claim' 
      });
      console.log('✅ Tracked: claim for campaign', campaign.id);
    }

    try {
      // Track engagement (additional API call if needed)
      await fetch('https://n8n.tenear.com/webhook/track-engagement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: campaign?.id,
          action: 'claim_offer',
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent
        })
      });
    } catch (error) {
      console.log('Error tracking engagement:', error);
    }

    // Show success message
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg z-50 shadow-lg';
    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
        </svg>
        <div>
          <div class="font-semibold">Offer Claimed!</div>
          <div class="text-sm">Show this to ${campaign?.shop?.name}</div>
        </div>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 5000);

    // Generate a claim code for the customer
    const claimCode = `CLAIM-${campaign?.id?.slice(-6)}-${Date.now().toString().slice(-6)}`;
    
    // Show claim details modal
    const claimModal = document.createElement('div');
    claimModal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    claimModal.innerHTML = `
      <div class="bg-white p-6 rounded-lg max-w-sm w-full mx-4 text-center">
        <div class="text-green-500 mb-4">
          <svg class="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
          </svg>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">Offer Claimed!</h3>
        <p class="text-gray-600 mb-4">Present this code to ${campaign?.shop?.name}</p>
        <div class="bg-gray-100 p-4 rounded-lg mb-4">
          <div class="text-2xl font-bold text-blue-600 tracking-wider">${claimCode}</div>
        </div>
        <div class="text-sm text-gray-500 mb-4">
          This offer is valid for today only
        </div>
        <button onclick="this.closest('.fixed').remove()" class="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700">
          Got it!
        </button>
      </div>
    `;
    document.body.appendChild(claimModal);
  };

  const handleCallShop = () => {
    // Track call action
    if (campaign?.shop_id && campaign?.id) {
      trackEvent({ 
        shop_id: campaign.shop_id, 
        campaign_id: campaign.id, 
        action: 'call' 
      });
      console.log('✅ Tracked: call for campaign', campaign.id);
    }

    if (campaign?.shop?.phone) {
      window.location.href = `tel:${campaign.shop.phone}`;
    }
  };

  const handleGetDirections = () => {
    // Track directions action
    if (campaign?.shop_id && campaign?.id) {
      trackEvent({ 
        shop_id: campaign.shop_id, 
        campaign_id: campaign.id, 
        action: 'directions' 
      });
      console.log('✅ Tracked: directions for campaign', campaign.id);
    }

    if (campaign?.shop?.location) {
      // Use Google Maps for directions
      const location = encodeURIComponent(campaign.shop.location);
      window.open(`https://maps.google.com/maps?q=${location}`, '_blank');
    } else {
      // Fallback to general mall location
      const mallLocation = encodeURIComponent(`${campaign?.zone} Mall`);
      window.open(`https://maps.google.com/maps?q=${mallLocation}`, '_blank');
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    console.log('CampaignViewer: Rendering loading state');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign...</p>
          <p className="text-xs text-gray-400 mt-2">Debug: Looking for campaign ID = {campaignId}</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    console.log('CampaignViewer: Rendering error state');
    console.log('CampaignViewer: Error:', error);
    console.log('CampaignViewer: Campaign:', campaign);
    console.log('CampaignViewer: Campaign ID:', campaignId);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Campaign Not Found</h2>
            <p className="text-gray-600 mb-4">
              {error || `The campaign "${campaignId}" does not exist or has expired.`}
            </p>
            <div className="space-y-2">
              <Button onClick={() => window.history.back()}>
                <ArrowLeft size={16} className="mr-2" />
                Go Back
              </Button>
              <div className="text-xs text-gray-400 mt-4">
                Debug: Campaign ID = {campaignId}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Safety fallback - always render something
  console.log('CampaignViewer: Final render with campaign:', campaign);
  
  if (!campaign) {
    console.log('CampaignViewer: Campaign is null, showing fallback');
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <h2 className="text-xl font-semibold text-red-900 mb-2">Debug Info</h2>
            <p className="text-gray-600 mb-4">Campaign object is null</p>
            <p className="text-xs text-gray-400">Campaign ID: {campaignId}</p>
            <p className="text-xs text-gray-400">Loading: {loading.toString()}</p>
            <p className="text-xs text-gray-400">Error: {error || 'None'}</p>
            <Button onClick={() => window.history.back()} className="mt-4">
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
            <ArrowLeft size={16} className="mr-1" />
            Back
          </Button>
          <h1 className="font-semibold text-gray-900">Campaign</h1>
          <Button variant="ghost" size="sm" onClick={handleShare}>
            <Share2 size={16} />
          </Button>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Shop Info */}
        {campaign.shop && (
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-gray-900">{campaign.shop.name}</h2>
                  <p className="text-sm text-blue-600 font-medium">
                    {campaign.shop.name} - {campaign.zone} Mall
                  </p>
                </div>
                <Badge variant="outline">{campaign.zone}</Badge>
              </div>
              {campaign.shop.description && (
                <p className="text-gray-600 text-sm mb-3">{campaign.shop.description}</p>
              )}
              <div className="flex gap-2">
                {campaign.shop.phone && (
                  <Button variant="outline" size="sm" className="flex-1" onClick={handleCallShop}>
                    <Phone size={14} className="mr-1" />
                    Call
                  </Button>
                )}
                {campaign.shop.location && (
                  <Button variant="outline" size="sm" className="flex-1" onClick={handleGetDirections}>
                    <Navigation size={14} className="mr-1" />
                    Directions
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Campaign Card */}
        <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="p-6">
            {/* Campaign Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h1 className="text-xl font-bold text-gray-900 mb-1">{campaign.name}</h1>
                <div className="text-sm font-medium text-blue-600 mb-2">
                  {campaign.shop?.name || 'Featured Shop'}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock size={14} />
                  <span>{getTimeAgo(campaign.created_at)}</span>
                  <MapPin size={14} className="ml-2" />
                  <span className="capitalize">{campaign.zone} Mall</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLiked(!liked)}
                className={`${liked ? 'text-red-500' : 'text-gray-400'}`}
              >
                <Heart size={20} fill={liked ? 'currentColor' : 'none'} />
              </Button>
            </div>

            {/* Campaign Message */}
            <div className="mb-6">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{campaign.message}</p>
            </div>

            {/* Campaign Actions */}
            <div className="flex gap-3">
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleClaimOffer}>
                <Star size={16} className="mr-2" />
                Claim Offer
              </Button>
              <Button variant="outline" onClick={handleShare}>
                <Share2 size={16} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Additional Content Based on Campaign Type */}
        <Card className="bg-white/80 backdrop-blur-sm">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">About This Campaign</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Campaign Type:</span>
                <span className="font-medium">Promotional</span>
              </div>
              <div className="flex justify-between">
                <span>Duration:</span>
                <span className="font-medium">Ongoing</span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <span className="font-medium text-green-600">Active</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Related Campaigns */}
        <Card className="bg-white/80 backdrop-blur-sm">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">More from {campaign.shop?.name}</h3>
            <p className="text-sm text-gray-600">
              Discover other exciting offers and events from this shop.
            </p>
            <Button variant="outline" size="sm" className="mt-3 w-full">
              View All Campaigns
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-xs text-gray-500">
            Scanned via QR code • {new Date().toLocaleDateString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Powered by Mall Management System
          </p>
        </div>
      </div>
    </div>
  );
}
